import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import { PHOTOBOOTHS } from '../mock';

export const Photobooths = () => {
  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-captiva-cream to-captiva-cream-dark">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Nos Photobooths
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Deux modèles premium conçus pour capturer les meilleurs moments de votre événement
          </p>
        </div>
      </section>

      {/* Photobooths Details */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-32">
          {PHOTOBOOTHS.map((booth, index) => (
            <div
              key={booth.id}
              className={`grid md:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'md:flex-row-reverse' : ''
              }`}
            >
              {/* Image Gallery with Carousel for both photobooths */}
              <div className={`${index % 2 === 1 ? 'md:order-2' : ''}`}>
                {booth.images && booth.images.length > 1 ? (
                  /* Carousel for both photobooths */
                  <div className="relative">
                    <Carousel className="w-full">
                      <CarouselContent>
                        {booth.images.map((img, imgIdx) => (
                          <CarouselItem key={imgIdx}>
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                              <img
                                src={img}
                                alt={`${booth.name} - Photo ${imgIdx + 1}`}
                                className="w-full h-[500px] object-cover"
                              />
                              {booth.premium && imgIdx === 0 && (
                                <div className="absolute top-6 right-6 bg-captiva-gold text-white px-6 py-3 rounded-full text-base font-semibold flex items-center space-x-2 shadow-lg">
                                  <Sparkles className="h-5 w-5" />
                                  <span>Premium</span>
                                </div>
                              )}
                            </div>
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                      <CarouselPrevious className="left-4" />
                      <CarouselNext className="right-4" />
                    </Carousel>
                    {/* Carousel indicators */}
                    <div className="flex justify-center mt-4 space-x-2">
                      {booth.images.map((_, idx) => (
                        <div key={idx} className="w-2 h-2 rounded-full bg-captiva-gold opacity-50" />
                      ))}
                    </div>
                  </div>
                ) : (
                  /* Fallback single image */
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                    <img
                      src={booth.image}
                      alt={booth.name}
                      className="w-full h-[500px] object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {booth.premium && (
                      <div className="absolute top-6 right-6 bg-captiva-gold text-white px-6 py-3 rounded-full text-base font-semibold flex items-center space-x-2 shadow-lg">
                        <Sparkles className="h-5 w-5" />
                        <span>Premium</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Content */}
              <div className={`${index % 2 === 1 ? 'md:order-1' : ''}`}>
                <div className="inline-flex items-center space-x-2 bg-captiva-cream text-captiva-beige-dark px-4 py-2 rounded-full text-sm font-medium mb-4">
                  <span>{booth.tagline}</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  {booth.name}
                </h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  {booth.description}
                </p>

                {/* Features */}
                <div className="space-y-4 mb-8">
                  {booth.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="h-6 w-6 text-captiva-gold flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Ideal For */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Idéal pour :</h3>
                  <div className="flex flex-wrap gap-2">
                    {booth.idealFor.map((event, idx) => (
                      <span
                        key={idx}
                        className="bg-captiva-cream text-captiva-beige-dark px-4 py-2 rounded-full text-sm font-medium border border-captiva-gold-light"
                      >
                        {event}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Pricing & CTA */}
                <Card className="p-6 bg-gradient-to-br from-captiva-cream to-captiva-cream-dark border-2 border-captiva-gold-light">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">{booth.priceLabel}</p>
                      <p className="text-4xl font-bold text-gray-900">{booth.startingPrice}€</p>
                      <p className="text-sm text-gray-500">TVAC</p>
                    </div>
                    <div className="space-y-2">
                      <Link to="/contact">
                        <Button className="bg-captiva-gold hover:bg-captiva-gold-dark text-white w-full">
                          Réserver
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                      <Link to="/tarifs">
                        <Button variant="outline" className="border-captiva-gold text-captiva-gold hover:bg-captiva-cream w-full">
                          Voir les tarifs
                        </Button>
                      </Link>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Besoin d'aide pour choisir ?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Contactez-nous et nous vous aiderons à sélectionner le photobooth parfait pour votre événement
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-captiva-gold hover:bg-captiva-gold-dark text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Demander conseil
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Photobooths;
